/**
 * ModuleRegistryAdmin — список 17 AI-модулей (read-only).
 * Показывает Janni, какие модули есть в системе.
 */
import { useTranslation } from 'react-i18next';
import {
  FileText,
  Search,
  Image as ImageIcon,
  Languages,
  Sparkles,
  HelpCircle,
  type LucideIcon,
} from 'lucide-react';

import { getModuleDisplayName, useModules } from '@/hooks/useAdmin';
import { cn } from '@/lib/utils';

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  content: FileText,
  search: Search,
  media: ImageIcon,
  translation: Languages,
  utility: Sparkles,
};

const CATEGORY_COLORS: Record<string, string> = {
  content: 'bg-primary-500/10 text-primary-400',
  search: 'bg-info-tint/10 text-info',
  media: 'bg-fuchsia-500/10 text-fuchsia-400',
  translation: 'bg-warning-tint/10 text-warning',
  utility: 'bg-success-tint/10 text-success',
};

export function ModuleRegistryAdmin() {
  const { i18n } = useTranslation();
  const { data: modules, isLoading } = useModules();

  if (isLoading) {
    return <div className="text-sm text-surface-400">Loading modules…</div>;
  }

  // Группируем по категории
  const byCategory: Record<string, NonNullable<typeof modules>> = {};
  (modules ?? []).forEach((m) => {
    if (!byCategory[m.category]) byCategory[m.category] = [];
    byCategory[m.category]!.push(m);
  });

  return (
    <div className="space-y-4">
      <p className="text-sm text-surface-400">
        17 AI-модулей, доступных в системе. Read-only — редактирование в Фазе 2+.
      </p>
      {Object.entries(byCategory).map(([cat, mods]) => {
        const Icon = CATEGORY_ICONS[cat] ?? HelpCircle;
        return (
          <div key={cat}>
            <div className="flex items-center gap-2 mb-2">
              <div
                className={cn(
                  'w-7 h-7 rounded-lg flex items-center justify-center',
                  CATEGORY_COLORS[cat],
                )}
              >
                <Icon className="w-4 h-4" />
              </div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-surface-300">
                {cat}
              </h3>
              <span className="text-xs text-surface-500">({mods?.length})</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {(mods ?? []).map((m) => (
                <div key={m.id} className="card flex items-center gap-3 p-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">
                      {getModuleDisplayName(m, i18n.language)}
                    </div>
                    <div className="text-xs text-surface-500 font-mono">{m.key}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {m.is_user_togglable && (
                      <span className="text-xs px-1.5 py-0.5 rounded-md bg-surface-800/60 text-surface-400">
                        toggle
                      </span>
                    )}
                    {m.is_active ? (
                      <span className="text-xs px-1.5 py-0.5 rounded-md bg-success-tint/10 text-success border border-success-tint/20">
                        on
                      </span>
                    ) : (
                      <span className="text-xs px-1.5 py-0.5 rounded-md bg-surface-700/30 text-surface-500">
                        off
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
